/**
 * Tells the branch that somebody is at the I-9 step [KAP-6].
 *
 * **Nothing here is allowed to make the candidate wait.** KAP-6 names this as the mechanism most likely to
 * grow a wait around it, so the call is fire-and-forget: it resolves whatever happens, reports nothing back,
 * and there is deliberately no outcome type for a caller to branch on. A failure is an operational concern
 * that the backend logs for ISS — never a candidate-facing event [decisions 0060, 0046].
 */
export type I9Event = 'reached' | 'assistance-requested';

import { kioskFetch } from './kioskFetch';

/*
 * ⭐ **The venue travels on the CREDENTIAL now, and this call names none** [03-us-kad-6c; decisions 0204,
 * 0221]. It used to send `venueId: PILOT_VENUE_ID`, and KOP-1 still scopes the recruiter queue by venue —
 * what changed is where the venue comes from: the api reads it off the kiosk session cookie `kioskFetch`
 * carries, and its `forbidNonWhitelisted` validation now answers `400 property venueId should not exist` to
 * a request that names one. The silence this file keeps by design [BR-K7] is why that 400 would never have
 * surfaced: the candidate waits, the recruiter's screen stays empty, and neither side sees an error.
 */

const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

export async function noticeI9(event: I9Event, candidateId?: number): Promise<void> {
  try {
    const response = await kioskFetch(`${apiBaseUrl()}/i9-notice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        ...(candidateId === undefined ? {} : { candidateId }),
      }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    /*
     * ⭐ **Read, and report — but still never raise.** This function swallowed every answer, which is right
     * for the CANDIDATE and wrong for everyone else: a 400 or a 401 here means the entry never landed, and
     * BR-K7 forbids telling the person at the kiosk, not telling an operator.
     */
    if (!response.ok) reportRefusedWrite('/i9-notice', response.status);
  } catch {
    /* Silent by design — see the note above. There is nobody at the kiosk to tell, and nothing to retry. */
  }
}

/**
 * Says out loud that a queue write was refused — ⛔ **to an OPERATOR, never to the candidate.**
 *
 * BR-K7 forbids holding somebody at an unattended kiosk over a staff-side failure, and this respects that:
 * the caller still gets nothing back and no screen changes. What it does not do any more is discard the
 * fact. ⚠️ **The wire shape of this call changed with 03-us-kad-6c** — `venueId` left the body and the venue
 * now travels on the credential — so an api that predates KAD-6a answers `400` and the candidate waits for
 * a recruiter nobody told. ⛔ **No candidate id and no factor**: this prints on a shared device's console.
 */
function reportRefusedWrite(path: string, status: number): void {
  console.error({
    msg: 'a recruiter-queue write was REFUSED — the candidate is waiting and nobody has been told',
    alert: 'kiosk_queue_write_failed',
    path,
    status,
  });
}
